import { Inbox } from "lucide-react";
import type { ReactNode } from "react";

import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OtcSellOrder } from "../../types/cryptoSell.types";
import { CryptoSellStatusPill } from "../sale/CryptoSellStatusPill";
import { MonoValue } from "../shared/CryptoSwapBits";
import { NetworkName } from "../shared/NetworkIcon";
import { SaleAmounts } from "./SaleAmounts";

export interface CryptoSellOrdersListProps {
  rows: OtcSellOrder[];
  isLoading: boolean;
  isFiltered: boolean;
  onOpen: (sale: OtcSellOrder) => void;
  footer?: ReactNode;
}

function SalesEmpty({ isFiltered, surface }: { isFiltered: boolean; surface?: "card" }) {
  const { t } = useTranslations("cryptoSwaps");
  return (
    <ColocaEmpty
      surface={surface}
      icon={Inbox}
      title={isFiltered ? t("sell_table_no_results_title") : t("sell_table_empty_title")}
      description={isFiltered ? t("table_no_results_description") : t("sell_table_empty_description")}
    />
  );
}

/** The sales on `lg` and up. */
export function CryptoSellOrdersTable({
  rows,
  isLoading,
  isFiltered,
  onOpen,
  footer,
}: CryptoSellOrdersListProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");

  const columns: ColocaDataTableColumn<OtcSellOrder>[] = [
    {
      id: "date",
      header: t("table_col_date"),
      headerClassName: "w-44",
      cell: (sale) => (
        <MonoValue className="text-xs text-muted-foreground">
          {formatDateTime(sale.created_at, { locale: activeLanguage })}
        </MonoValue>
      ),
    },
    {
      id: "operation",
      header: t("sell_table_col_operation"),
      cell: (sale) => <SaleAmounts sale={sale} />,
    },
    {
      id: "network",
      header: t("summary_network"),
      headerClassName: "w-44",
      cell: (sale) => <NetworkName network={sale.deposit.network} className="text-[13px]" />,
    },
    {
      id: "status",
      header: t("table_col_status"),
      headerClassName: "w-44 text-right",
      cellClassName: "text-right",
      cell: (sale) => <CryptoSellStatusPill status={sale.status} />,
    },
  ];

  return (
    <ColocaDataTable
      columns={columns}
      rows={rows}
      rowKey={(sale) => sale.id}
      onRowClick={onOpen}
      loading={isLoading}
      footer={footer}
      empty={<SalesEmpty isFiltered={isFiltered} surface="card" />}
    />
  );
}

/** The same sales below `lg`: one card per sale. */
export function CryptoSellOrdersCards({ rows, isLoading, isFiltered, onOpen }: CryptoSellOrdersListProps) {
  const { activeLanguage } = useTranslations("cryptoSwaps");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2.5">
        {Array.from({ length: 4 }, (_, index) => (
          <ColocaSkeleton key={index} className="h-24 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) return <SalesEmpty isFiltered={isFiltered} />;

  return (
    <ul className="flex list-none flex-col gap-2.5 p-0">
      {rows.map((sale) => (
        <li key={sale.id}>
          <button
            type="button"
            onClick={() => onOpen(sale)}
            className={cn(
              colocaSurfaceVariants({ variant: "panel", padding: "md", interactive: true }),
              "flex w-full flex-col gap-2.5",
            )}
          >
            <span className="flex flex-wrap items-center justify-between gap-2">
              <MonoValue className="text-xs text-muted-foreground">
                {formatDateTime(sale.created_at, { locale: activeLanguage })}
              </MonoValue>
              <CryptoSellStatusPill status={sale.status} size="sm" />
            </span>
            <SaleAmounts sale={sale} size="sm" />
            <NetworkName network={sale.deposit.network} size={14} className="text-xs text-muted-foreground" />
          </button>
        </li>
      ))}
    </ul>
  );
}
